import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export type GoldDustLoaderSize = 'sm' | 'md' | 'lg';

export interface GoldDustLoaderProps {
  /**
   * `sm` 16px (buttons, inputs, selects — default), `md` 20px (cards, drawers),
   * `lg` 24px (small panels, refetch indicators). Never below 16px.
   */
  size?: GoldDustLoaderSize;
  /** Visually-hidden status label. Defaults to the i18n "Carregando". */
  label?: string;
  className?: string;
}

const PARTICLES = [0, 1, 2, 3, 4, 5] as const;

/**
 * "Gold Dust" — the design-system micro loader (ALC-3327).
 *
 * Six gold-dust shards on irregular radii converge into one solid dot, pulse,
 * dissolve and repeat. Pure CSS: all geometry and keyframes live in
 * `index.css` under `.gold-dust-loader`, so dozens of instances stay cheap.
 * Colour is `currentColor`, so it inherits from the surrounding text — gold
 * where the context is gold, `primary-foreground` inside a primary button.
 *
 * Under `prefers-reduced-motion: reduce` the glyph is static (solid core with
 * six faint particles). A parent `<button>` should also set `aria-busy`; the
 * shared Button `loading` prop does this for you.
 */
export function GoldDustLoader({ size = 'sm', label, className }: GoldDustLoaderProps) {
  const { t } = useTranslation('commonComponents');
  const resolvedLabel = label ?? t('goldDustLoader.label');

  return (
    <span role="status" data-size={size} className={cn('gold-dust-loader', className)}>
      {PARTICLES.map((index) => (
        <span key={index} aria-hidden="true" className="gold-dust-loader__particle" />
      ))}
      <span aria-hidden="true" className="gold-dust-loader__core" />
      <span className="sr-only">{resolvedLabel}</span>
    </span>
  );
}
